<script lang="ts">
    import { onDestroy, onMount } from "svelte";
    import type { EnableCameraScene } from "../../Phaser/Login/EnableCameraScene";
    import { EnableCameraSceneName } from "../../Phaser/Login/EnableCameraScene";
    import {
        requestedCameraDeviceIdStore,
        batchGetUserMediaStore,
        cameraListStore,
        localVolumeStore,
        requestedMicrophoneDeviceIdStore,
        microphoneListStore,
        speakerListStore,
        requestedCameraState,
        requestedMicrophoneState,
        speakerSelectedStore,
        localStreamStore,
    } from "../../Stores/MediaStore";
    import type { Game } from "../../Phaser/Game/Game";
    import { LL, locale } from "../../../i18n/i18n-svelte";
    import { myCameraStore, myMicrophoneStore } from "../../Stores/MyMediaStore";
    import { localUserStore } from "../../Connection/LocalUserStore";
    import { gameManager } from "../../Phaser/Game/GameManager";

    import bgMap from "../images/map-exemple.png";
    import HorizontalSoundMeterWidget from "./HorizontalSoundMeterWidget.svelte";
    import SelectMicrophone from "./SelectMicrophone.svelte";
    import SelectCamera from "./SelectCamera.svelte";
    import SelectSpeaker from "./SelectSpeaker.svelte";
    import { IconMicrophoneOn, IconCamera } from "@wa-icons";

    interface Props {
        game: Game;
    }

    let { game }: Props = $props();

    let enableCameraScene = $derived(game.scene.getScene(EnableCameraSceneName) as EnableCameraScene);
    const bgColor = gameManager.currentStartedRoom.backgroundColor ?? "#151515";
    let legals = gameManager.currentStartedRoom?.legals ?? {};

    let selectedCamera: string | undefined = $state(undefined);
    let selectedMicrophone: string | undefined = $state(undefined);
    const sound = new Audio("/resources/objects/webrtc-in.mp3");

    let legalStrings: string[] = [];
    if (legals?.termsOfUseUrl) {
        legalStrings.push(
            '<a href="' +
                encodeURI(legals.termsOfUseUrl) +
                '" target="_blank" class="text-white no-underline hover:underline bold hover:text-white">' +
                $LL.login.termsOfUse() +
                "</a>",
        );
    }
    if (legals?.privacyPolicyUrl) {
        legalStrings.push(
            '<a href="' +
                encodeURI(legals.privacyPolicyUrl) +
                '" target="_blank" class="text-white no-underline hover:underline bold hover:text-white">' +
                $LL.login.privacyPolicy() +
                "</a>",
        );
    }
    if (legals?.cookiePolicyUrl) {
        legalStrings.push(
            '<a href="' +
                encodeURI(legals.cookiePolicyUrl) +
                '" target="_blank" class="text-white no-underline hover:underline bold hover:text-white">' +
                $LL.login.cookiePolicy() +
                "</a>",
        );
    }

    let legalString: string | undefined = $state();
    if (legalStrings.length > 0) {
        if (Intl.ListFormat) {
            const formatter = new Intl.ListFormat($locale, { style: "long", type: "conjunction" });
            legalString = formatter.format(legalStrings);
        } else {
            // For old browsers
            legalString = legalStrings.join(", ");
        }
    }

    function submit() {
        selectCamera(selectedCamera);
        selectMicrophone(selectedMicrophone);
        enableCameraScene.login();
    }

    function srcObject(node: HTMLVideoElement, stream: MediaStream) {
        node.srcObject = stream;
        return {
            update(newStream: MediaStream) {
                if (node.srcObject != newStream) {
                    node.srcObject = newStream;
                }
            },
        };
    }

    let stream: MediaStream | undefined;

    const unsubscribeLocalStreamStore = localStreamStore.subscribe((value) => {
        if (value.type === "success") {
            stream = value.stream;

            if (stream !== undefined) {
                const videoTracks = stream.getVideoTracks();
                if (videoTracks.length > 0) {
                    selectedCamera = videoTracks[0].getSettings().deviceId;
                } else {
                    selectedCamera = undefined;
                }
                const audioTracks = stream.getAudioTracks();
                if (audioTracks.length > 0) {
                    selectedMicrophone = audioTracks[0].getSettings().deviceId;
                } else {
                    selectedMicrophone = undefined;
                }
            }
        } else {
            stream = undefined;
            selectedCamera = undefined;
            selectedMicrophone = undefined;
        }
    });

    onDestroy(() => {
        unsubscribeLocalStreamStore();
    });

    onMount(() => {
        //init the component to enable webcam and microphone
        batchGetUserMediaStore.startBatch();
        myCameraStore.set(true);
        myMicrophoneStore.set(true);
        requestedCameraState.enableWebcam();

        requestedMicrophoneState.enableMicrophone();

        batchGetUserMediaStore.commitChanges();
        sound.load();
    });

    function handleSelectCamera(deviceId: string | undefined) {
        selectCamera(deviceId);
    }

    function handleSelectMicrophone(device: string | undefined) {
        selectMicrophone(device);
    }

    function handleSelectSpeaker(deviceId: string | undefined) {
        selectSpeaker(deviceId);
    }

    function selectCamera(newCameraSelected: string | undefined = undefined) {
        selectedCamera = newCameraSelected;
        if (!selectedCamera) {
            localUserStore.setPreferredVideoInputDevice("");
            requestedCameraState.disableWebcam();
            return;
        }
        requestedCameraState.enableWebcam();
        requestedCameraDeviceIdStore.set(selectedCamera);
        localUserStore.setPreferredVideoInputDevice(selectedCamera);
    }

    function selectMicrophone(newMicrophoneSelected: string | undefined = undefined) {
        selectedMicrophone = newMicrophoneSelected;
        if (!selectedMicrophone) {
            localUserStore.setPreferredAudioInputDevice("");
            requestedMicrophoneState.disableMicrophone();
            return;
        }
        requestedMicrophoneState.enableMicrophone();
        requestedMicrophoneDeviceIdStore.set(selectedMicrophone);
        localUserStore.setPreferredAudioInputDevice(selectedMicrophone);
    }

    function selectSpeaker(deviceId: string | undefined) {
        localUserStore.setSpeakerDeviceId(deviceId ?? "");
        speakerSelectedStore.set(deviceId);
    }

    function playSoundClick() {
        sound.play().catch((e) => console.error(e));
    }
    /* eslint-disable svelte/no-at-html-tags */
</script>

<form
    class="enableCameraScene nutrivera-entry-screen pointer-events-auto relative z-30 m-0 min-h-dvh px-3 py-6"
    onsubmit={(event) => {
        event.preventDefault();
        submit();
    }}
>
    <section class="mx-auto flex min-h-[calc(100dvh-48px)] max-w-6xl flex-col justify-center gap-5 pb-24">
        <div class="relative flex w-full flex-col items-center justify-center overflow-hidden text-white">
            <section class="mb-4 text-center">
                <h2 class="text-2xl font-semibold">{$LL.camera.enable.title()}</h2>
                <p class="nutrivera-entry-muted mx-auto mt-2 hidden max-w-2xl text-sm md:block">
                    {$LL.camera.enable.start()}
                </p>
            </section>

            <div
                class="grid w-full items-start gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]"
            >
                <!-- MICROPHONE -->

                <SelectMicrophone
                    onselectdevice={handleSelectMicrophone}
                    deviceList={$microphoneListStore ?? []}
                    selectedDevice={selectedMicrophone}
                >
                    {#snippet icon()}
                        <IconMicrophoneOn font-size="24" />
                    {/snippet}
                    {#snippet title()}
                        <span>{$LL.actionbar.subtitle.microphone()}</span>
                    {/snippet}
                    {#snippet widget()}
                        <div class="absolute top-4 start-0 flex justify-center w-full">
                            <HorizontalSoundMeterWidget spectrum={$localVolumeStore} />
                        </div>
                    {/snippet}
                </SelectMicrophone>

                <!-- CAMERA -->
                <SelectCamera
                    onselectdevice={handleSelectCamera}
                    deviceList={$cameraListStore ?? []}
                    selectedDevice={selectedCamera}
                >
                    {#snippet icon()}
                        <IconCamera font-size="24" />
                    {/snippet}
                    {#snippet title()}
                        <span>{$LL.camera.editCam()}</span>
                    {/snippet}
                    {#snippet widget()}
                        <span>
                            {#if selectedCamera !== undefined && $localStreamStore.type === "success" && $localStreamStore.stream}
                                <video
                                    class="myCamVideoSetup nutrivera-entry-stage !max-h-none !w-full flex aspect-video scale-x-[-1] items-center justify-center overflow-hidden"
                                    use:srcObject={$localStreamStore.stream}
                                    autoplay
                                    muted
                                    playsinline
                                ></video>
                            {:else}
                                <div
                                    class="webrtcsetup nutrivera-entry-stage !max-h-none !w-full flex aspect-video items-center justify-center overflow-hidden"
                                >
                                    CAM PB <!-- TODO HUGO : catch pb with cam -->
                                </div>
                            {/if}
                        </span>
                    {/snippet}
                </SelectCamera>

                <!-- SPEAKER -->
                {#if $speakerSelectedStore != undefined && $speakerListStore && $speakerListStore.length > 0}
                    <SelectSpeaker
                        onplaysound={playSoundClick}
                        onselectdevice={handleSelectSpeaker}
                        deviceList={$speakerListStore ?? []}
                        selectedDevice={$speakerSelectedStore}
                    />
                {/if}
            </div>
            <div
                class="fixed inset-x-0 bottom-0 z-40 border-t border-solid border-[var(--nutrivera-shell-border)] bg-[var(--nutrivera-shell-bg)] pb-[env(safe-area-inset-bottom)]"
            >
                <section class="mx-auto flex max-w-3xl flex-col-reverse gap-2 p-3 sm:flex-row sm:p-4">
                    <button
                        type="submit"
                        class="nutrivera-entry-secondary hidden w-full px-4 md:w-1/2">{$LL.actionbar.cancel()}</button
                    >
                    <!-- TODO ACTION -->
                    <button type="submit" class="nutrivera-entry-primary block w-full px-4 font-semibold md:w-1/2"
                        >{$LL.menu.settings.save()}</button
                    >
                </section>
                {#if legalString}
                    <section class="terms-and-conditions h-fit z-40 text-center w-full">
                        <a style="display: none;" href="traduction">Need for traduction</a>
                        <p class="nutrivera-entry-muted text-xs leading-relaxed">
                            {@html $LL.login.terms({
                                links: legalString,
                            })}
                        </p>
                    </section>
                {/if}
            </div>
        </div>
    </section>
</form>
<div class="fixed inset-0 z-10 h-dvh w-dvw bg-cover bg-center" style="background-image: url('{bgMap}');"></div>
<div
    class="fixed inset-0 z-20 h-dvh w-dvw bg-[var(--nutrivera-shell-bg)] opacity-[0.88]"
    style="background-color: '{bgColor}';"
></div>

<style>
    .enableCameraScene {
        h2 {
            margin: 1px;
        }

        section.text-center {
            text-align: center;
        }
    }
</style>
